import {
  completionYMMarks,
  completionYMs,
  dateMax,
  dateMin,
  estatePriceMax,
  estatePriceMin,
  roomsMarks,
  roomsMax,
  roomsMin,
  squareAreaOfMansionMarks,
  squareAreaOfMansionMax,
  squareAreaOfMansionMin,
  tsuboAreaMarks,
  tsuboAreaMax,
  tsuboAreaMin
} from "common/consts/estates";
import { useAutoReducer } from "common/functions/reducers";
import { BldConditionSelection } from "common/interfaces/BldConditionSelection";
import { EstateFilterConditions } from "common/interfaces/EstateFilterConditions";
import { PurposeSummaryB } from "common/interfaces/PurposeSummaryB";
import { StructureSummary } from "common/interfaces/StructureSummary";
import { useJsonMasterHooks } from "hooks/jsonMasterHooks";
import { useSliderThemeHooks } from "hooks/sliderThemeHooks";
import { useUserHooks } from "hooks/userHooks";
import { MouseEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { EstateFilterDialogProps } from "./interfaces";

export const useEstateFilterDialogHooks = (props: EstateFilterDialogProps) => {
  const titRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const { sliderTheme } = useSliderThemeHooks();

  const { busy: busyOfUser } = useUserHooks(props.username, props.setFatal);

  const [filterConditions, , forceFilterConditions, setFilterConditions] =
    useAutoReducer<EstateFilterConditions>(props.filterConditions);

  const { jsonMasters: bldConditionSelections } =
    useJsonMasterHooks<BldConditionSelection>(
      "bld_condition_selections",
      props.setFatal
    );

  const { jsonMasters: structureSummaries } =
    useJsonMasterHooks<StructureSummary>("structure_summaries", props.setFatal);

  const { jsonMasters: purposeSummaryBs } = useJsonMasterHooks<PurposeSummaryB>(
    "purpose_summary_bs",
    props.setFatal
  );

  const formattedEstatePriceFrom = useMemo(
    () =>
      filterConditions.estatePriceFrom === estatePriceMin
        ? "下限なし"
        : filterConditions.estatePriceFrom.toLocaleString(),
    [filterConditions.estatePriceFrom]
  );

  const formattedEstatePriceTo = useMemo(
    () =>
      filterConditions.estatePriceTo === estatePriceMax
        ? "上限なし"
        : filterConditions.estatePriceTo.toLocaleString(),
    [filterConditions.estatePriceTo]
  );

  const { tsuboAreaFromIndex, tsuboAreaToIndex } = useMemo(
    () =>
      ({
        tsuboAreaFromIndex: tsuboAreaMarks.findIndex(
          (x) => x.value === filterConditions.tsuboAreaFrom
        ),
        tsuboAreaToIndex: tsuboAreaMarks.findIndex(
          (x) => x.value === filterConditions.tsuboAreaTo
        )
      }) as const,
    [filterConditions.tsuboAreaFrom, filterConditions.tsuboAreaTo]
  );

  const formattedTsuboAreaFrom = useMemo(
    () =>
      filterConditions.tsuboAreaFrom === tsuboAreaMin
        ? "下限なし"
        : filterConditions.tsuboAreaFrom.toLocaleString(),
    [filterConditions.tsuboAreaFrom]
  );

  const formattedTsuboAreaTo = useMemo(
    () =>
      filterConditions.tsuboAreaTo === tsuboAreaMax
        ? "上限なし"
        : filterConditions.tsuboAreaTo.toLocaleString(),
    [filterConditions.tsuboAreaTo]
  );

  const { squareAreaOfMansionFromIndex, squareAreaOfMansionToIndex } = useMemo(
    () =>
      ({
        squareAreaOfMansionFromIndex: squareAreaOfMansionMarks.findIndex(
          (x) => x.value === filterConditions.squareAreaOfMansionFrom
        ),
        squareAreaOfMansionToIndex: squareAreaOfMansionMarks.findIndex(
          (x) => x.value === filterConditions.squareAreaOfMansionTo
        )
      }) as const,
    [
      filterConditions.squareAreaOfMansionFrom,
      filterConditions.squareAreaOfMansionTo
    ]
  );

  const formattedSquareAreaOfMansionFrom = useMemo(
    () =>
      filterConditions.squareAreaOfMansionFrom === squareAreaOfMansionMin
        ? "下限なし"
        : filterConditions.squareAreaOfMansionFrom.toLocaleString(),
    [filterConditions.squareAreaOfMansionFrom]
  );

  const formattedSquareAreaOfMansionTo = useMemo(
    () =>
      filterConditions.squareAreaOfMansionTo === squareAreaOfMansionMax
        ? "上限なし"
        : filterConditions.squareAreaOfMansionTo.toLocaleString(),
    [filterConditions.squareAreaOfMansionTo]
  );

  const { completionYMFromIndex, completionYMToIndex } = useMemo(
    () =>
      ({
        completionYMFromIndex: completionYMs.findIndex(
          (x) => x === filterConditions.completionYMTo
        ),
        completionYMToIndex: completionYMs.findIndex(
          (x) => x === filterConditions.completionYMFrom
        )
      }) as const,
    [filterConditions.completionYMFrom, filterConditions.completionYMTo]
  );

  const formattedCompletionYMFrom = useMemo(
    () =>
      completionYMFromIndex === 0
        ? "新築"
        : (completionYMMarks.at(completionYMFromIndex)?.label ?? ""),
    [completionYMFromIndex]
  );

  const formattedCompletionYMTo = useMemo(
    () =>
      completionYMToIndex === completionYMs.length - 1
        ? "築年指定なし"
        : (completionYMMarks.at(completionYMToIndex)?.label ?? ""),
    [completionYMToIndex]
  );

  const { roomsFromIndex, roomsToIndex } = useMemo(
    () =>
      ({
        roomsFromIndex: roomsMarks.findIndex(
          (x) => x.value === filterConditions.roomsFrom
        ),
        roomsToIndex: roomsMarks.findIndex(
          (x) => x.value === filterConditions.roomsTo
        )
      }) as const,
    [filterConditions.roomsFrom, filterConditions.roomsTo]
  );

  const formattedRoomsFrom = useMemo(
    () =>
      filterConditions.roomsFrom === roomsMin
        ? "下限なし"
        : filterConditions.roomsFrom.toLocaleString(),
    [filterConditions.roomsFrom]
  );

  const formattedRoomsTo = useMemo(
    () =>
      filterConditions.roomsTo === roomsMax
        ? "上限なし"
        : filterConditions.roomsTo.toLocaleString(),
    [filterConditions.roomsTo]
  );

  const resetButtonOn = useMemo(() => {
    return (
      filterConditions.estatePriceFrom !==
        props.defaultFilterConditions.estatePriceFrom ||
      filterConditions.estatePriceTo !==
        props.defaultFilterConditions.estatePriceTo ||
      filterConditions.tsuboAreaFrom !==
        props.defaultFilterConditions.tsuboAreaFrom ||
      filterConditions.tsuboAreaTo !==
        props.defaultFilterConditions.tsuboAreaTo ||
      filterConditions.squareAreaOfMansionFrom !==
        props.defaultFilterConditions.squareAreaOfMansionFrom ||
      filterConditions.squareAreaOfMansionTo !==
        props.defaultFilterConditions.squareAreaOfMansionTo ||
      filterConditions.completionYMFrom !==
        props.defaultFilterConditions.completionYMFrom ||
      filterConditions.completionYMTo !==
        props.defaultFilterConditions.completionYMTo ||
      (filterConditions.bldConditionSelections.length ===
        props.defaultFilterConditions.bldConditionSelections.length &&
        props.defaultFilterConditions.bldConditionSelections
          .map((x) => x.id)
          .every((x) =>
            filterConditions.bldConditionSelections.map((x) => x.id).includes(x)
          )) === false ||
      filterConditions.petOk !== props.defaultFilterConditions.petOk ||
      filterConditions.flat !== props.defaultFilterConditions.flat ||
      filterConditions.forRent !== props.defaultFilterConditions.forRent ||
      filterConditions.exceptForRent !==
        props.defaultFilterConditions.exceptForRent ||
      filterConditions.venderOnly !==
        props.defaultFilterConditions.venderOnly ||
      filterConditions.companyRecNo !==
        props.defaultFilterConditions.companyRecNo ||
      filterConditions.structureSummaries.length !==
        structureSummaries.length ||
      (filterConditions.purposeSummaryBs.length ===
        props.defaultFilterConditions.purposeSummaryBs.length &&
        props.defaultFilterConditions.purposeSummaryBs
          .map((x) => x.id)
          .every((x) =>
            filterConditions.purposeSummaryBs.map((x) => x.id).includes(x)
          )) === false ||
      filterConditions.alsoCreatedAtFrom !== dateMin ||
      filterConditions.alsoCreatedAtTo !== dateMax ||
      filterConditions.alsoPricedAtFrom !== dateMin ||
      filterConditions.alsoPricedAtTo !== dateMax ||
      filterConditions.roomsFrom !== props.defaultFilterConditions.roomsFrom ||
      filterConditions.roomsTo !== props.defaultFilterConditions.roomsTo
    );
  }, [
    filterConditions,
    props.defaultFilterConditions,
    structureSummaries.length
  ]);

  const okButtonOn = useMemo(
    () =>
      filterConditions.estatePriceFrom !==
        props.filterConditions.estatePriceFrom ||
      filterConditions.estatePriceTo !== props.filterConditions.estatePriceTo ||
      filterConditions.tsuboAreaFrom !== props.filterConditions.tsuboAreaFrom ||
      filterConditions.tsuboAreaTo !== props.filterConditions.tsuboAreaTo ||
      filterConditions.squareAreaOfMansionFrom !==
        props.filterConditions.squareAreaOfMansionFrom ||
      filterConditions.squareAreaOfMansionTo !==
        props.filterConditions.squareAreaOfMansionTo ||
      filterConditions.completionYMFrom !==
        props.filterConditions.completionYMFrom ||
      filterConditions.completionYMTo !==
        props.filterConditions.completionYMTo ||
      filterConditions.bldConditionSelections.length !==
        props.filterConditions.bldConditionSelections.length ||
      filterConditions.petOk !== props.filterConditions.petOk ||
      filterConditions.flat !== props.filterConditions.flat ||
      filterConditions.forRent !== props.filterConditions.forRent ||
      filterConditions.exceptForRent !== props.filterConditions.exceptForRent ||
      filterConditions.venderOnly !== props.filterConditions.venderOnly ||
      filterConditions.companyRecNo !== props.filterConditions.companyRecNo ||
      filterConditions.structureSummaries.length !==
        props.filterConditions.structureSummaries.length ||
      filterConditions.purposeSummaryBs.length !==
        props.filterConditions.purposeSummaryBs.length ||
      filterConditions.alsoCreatedAtFrom !==
        props.filterConditions.alsoCreatedAtFrom ||
      filterConditions.alsoCreatedAtTo !==
        props.filterConditions.alsoCreatedAtTo ||
      filterConditions.alsoPricedAtFrom !==
        props.filterConditions.alsoPricedAtFrom ||
      filterConditions.alsoPricedAtTo !== props.filterConditions.alsoPricedAtTo,
    [
      filterConditions.alsoCreatedAtFrom,
      filterConditions.alsoCreatedAtTo,
      filterConditions.alsoPricedAtFrom,
      filterConditions.alsoPricedAtTo,
      filterConditions.bldConditionSelections.length,
      filterConditions.companyRecNo,
      filterConditions.completionYMFrom,
      filterConditions.completionYMTo,
      filterConditions.estatePriceFrom,
      filterConditions.estatePriceTo,
      filterConditions.exceptForRent,
      filterConditions.venderOnly,
      filterConditions.flat,
      filterConditions.forRent,
      filterConditions.petOk,
      filterConditions.purposeSummaryBs.length,
      filterConditions.squareAreaOfMansionFrom,
      filterConditions.squareAreaOfMansionTo,
      filterConditions.structureSummaries.length,
      filterConditions.tsuboAreaFrom,
      filterConditions.tsuboAreaTo,
      props.filterConditions.alsoCreatedAtFrom,
      props.filterConditions.alsoCreatedAtTo,
      props.filterConditions.alsoPricedAtFrom,
      props.filterConditions.alsoPricedAtTo,
      props.filterConditions.bldConditionSelections.length,
      props.filterConditions.companyRecNo,
      props.filterConditions.completionYMFrom,
      props.filterConditions.completionYMTo,
      props.filterConditions.estatePriceFrom,
      props.filterConditions.estatePriceTo,
      props.filterConditions.exceptForRent,
      props.filterConditions.venderOnly,
      props.filterConditions.flat,
      props.filterConditions.forRent,
      props.filterConditions.petOk,
      props.filterConditions.purposeSummaryBs.length,
      props.filterConditions.squareAreaOfMansionFrom,
      props.filterConditions.squareAreaOfMansionTo,
      props.filterConditions.structureSummaries.length,
      props.filterConditions.tsuboAreaFrom,
      props.filterConditions.tsuboAreaTo
    ]
  );

  const onEstatePriceChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const estatePrice = value as number[];
      setFilterConditions.estatePriceFrom(estatePrice[0]);
      setFilterConditions.estatePriceTo(estatePrice[1]);
    },
    [setFilterConditions]
  );

  const onTsuboAreaChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const tsuboAreaIndices = value as number[];

      setFilterConditions.tsuboAreaFrom(
        tsuboAreaMarks[tsuboAreaIndices[0]].value
      );
      setFilterConditions.tsuboAreaTo(
        tsuboAreaMarks[tsuboAreaIndices[1]].value
      );
    },
    [setFilterConditions]
  );

  const onSquareAreaOfMansionChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const squareAreaOfMansionIndices = value as number[];

      setFilterConditions.squareAreaOfMansionFrom(
        squareAreaOfMansionMarks[squareAreaOfMansionIndices[0]].value
      );
      setFilterConditions.squareAreaOfMansionTo(
        squareAreaOfMansionMarks[squareAreaOfMansionIndices[1]].value
      );
    },
    [setFilterConditions]
  );

  const onCompletionYMIndexChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const completionYMIndices = value as number[];

      setFilterConditions.completionYMFrom(
        completionYMs[completionYMIndices[1]]
      );
      setFilterConditions.completionYMTo(completionYMs[completionYMIndices[0]]);
    },
    [setFilterConditions]
  );

  const onRoomsChange: (
    event: Event,
    value: number | number[],
    activeThumb: number
  ) => void = useCallback(
    (_1, value) => {
      const roomsIndices = value as number[];

      setFilterConditions.roomsFrom(roomsMarks[roomsIndices[0]].value);
      setFilterConditions.roomsTo(roomsMarks[roomsIndices[1]].value);
    },
    [setFilterConditions]
  );

  const onBldConditionSelectionsChange = useCallback(
    (values: BldConditionSelection[]) => {
      setFilterConditions.bldConditionSelections(values);
    },
    [setFilterConditions]
  );

  const onPetOkChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.petOk(checked);
    },
    [setFilterConditions]
  );

  const onFlatChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.flat(checked);
    },
    [setFilterConditions]
  );

  const onForRentChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.forRent(checked);

      if (checked) {
        setFilterConditions.exceptForRent(false);
      }
    },
    [setFilterConditions]
  );

  const onExceptForRentChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.exceptForRent(checked);

      if (checked) {
        setFilterConditions.forRent(false);
      }
    },
    [setFilterConditions]
  );

  const onMyCompanyOnlyChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.companyRecNo(
        checked && props.companyRecNo ? props.companyRecNo : 0
      );
    },
    [props.companyRecNo, setFilterConditions]
  );

  const onVenderOnlyChange: (checked: boolean) => void = useCallback(
    (checked) => {
      setFilterConditions.venderOnly(checked);
    },
    [setFilterConditions]
  );

  const onStructureSummariesChange: (
    structureSummaries: StructureSummary[]
  ) => void = useCallback(
    (structureSummaries) => {
      setFilterConditions.structureSummaries(structureSummaries);
    },
    [setFilterConditions]
  );

  const onPurposeSummaryBsChange: (
    purposeSummaryBs: PurposeSummaryB[]
  ) => void = useCallback(
    (purposeSummaryBs) => {
      setFilterConditions.purposeSummaryBs(purposeSummaryBs);
    },
    [setFilterConditions]
  );

  const onAlsoCreatedAtFromChange = useCallback(
    (value: number | null | undefined) => {
      setFilterConditions.alsoCreatedAtFrom(value ?? dateMin);
    },
    [setFilterConditions]
  );

  const onAlsoCreatedAtToChange = useCallback(
    (value: number | null | undefined) => {
      const newValue =
        value === null || value === undefined
          ? dateMax
          : new Date(new Date(value).setHours(23, 59, 59, 998)).getTime();

      setFilterConditions.alsoCreatedAtTo(newValue);
    },
    [setFilterConditions]
  );

  const onAlsoPricedAtFromChange = useCallback(
    (value: number | null | undefined) => {
      setFilterConditions.alsoPricedAtFrom(value ?? dateMin);
    },
    [setFilterConditions]
  );

  const onAlsoPricedAtToChange = useCallback(
    (value: number | null | undefined) => {
      const newValue =
        value === null || value === undefined
          ? dateMax
          : new Date(new Date(value).setHours(23, 59, 59, 998)).getTime();

      setFilterConditions.alsoPricedAtTo(newValue);
    },
    [setFilterConditions]
  );

  const onResetButtonClick = useCallback(() => {
    forceFilterConditions({
      ...props.filterConditions,
      estatePriceFrom: props.defaultFilterConditions.estatePriceFrom,
      estatePriceTo: props.defaultFilterConditions.estatePriceTo,
      tsuboAreaFrom: props.defaultFilterConditions.tsuboAreaFrom,
      tsuboAreaTo: props.defaultFilterConditions.tsuboAreaTo,
      completionYMFrom: props.defaultFilterConditions.completionYMFrom,
      completionYMTo: props.defaultFilterConditions.completionYMTo,
      roomsFrom: props.defaultFilterConditions.roomsFrom,
      roomsTo: props.defaultFilterConditions.roomsTo,
      squareAreaOfMansionFrom:
        props.defaultFilterConditions.squareAreaOfMansionFrom,
      squareAreaOfMansionTo:
        props.defaultFilterConditions.squareAreaOfMansionTo,
      bldConditionSelections:
        props.defaultFilterConditions.bldConditionSelections,
      petOk: props.defaultFilterConditions.petOk,
      flat: props.defaultFilterConditions.flat,
      forRent: props.defaultFilterConditions.forRent,
      exceptForRent: props.defaultFilterConditions.exceptForRent,
      companyRecNo: props.defaultFilterConditions.companyRecNo,
      venderOnly: props.defaultFilterConditions.venderOnly,
      structureSummaries: props.defaultFilterConditions.structureSummaries,
      purposeSummaryBs: props.defaultFilterConditions.purposeSummaryBs,
      alsoCreatedAtFrom: props.defaultFilterConditions.alsoCreatedAtFrom,
      alsoCreatedAtTo: props.defaultFilterConditions.alsoCreatedAtTo,
      alsoPricedAtFrom: props.defaultFilterConditions.alsoPricedAtFrom,
      alsoPricedAtTo: props.defaultFilterConditions.alsoPricedAtTo
    });
  }, [
    forceFilterConditions,
    props.defaultFilterConditions,
    props.filterConditions
  ]);

  const onOkButtonClick = useCallback(() => {
    props.onOkButtonClick(filterConditions);
    if (props.onClose) {
      props.onClose();
    }
  }, [filterConditions, props]);

  const onBackPaperClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (titRef.current && boxRef.current) {
        if (
          titRef.current.contains(event.target as Node) ||
          boxRef.current.contains(event.target as Node)
        ) {
          return;
        }

        if (props.onClose) {
          props.onClose();
        }
      }
    },
    [props]
  );

  useEffect(() => {
    forceFilterConditions(props.filterConditions);
  }, [forceFilterConditions, props.filterConditions, props.open]);

  return {
    sliderTheme,

    titRef,
    boxRef,

    busy: busyOfUser,

    filterConditions,
    bldConditionSelections,
    structureSummaries,
    purposeSummaryBs,

    formattedEstatePriceFrom,
    formattedEstatePriceTo,

    formattedTsuboAreaFrom,
    formattedTsuboAreaTo,

    tsuboAreaFromIndex,
    tsuboAreaToIndex,

    formattedSquareAreaOfMansionFrom,
    formattedSquareAreaOfMansionTo,

    squareAreaOfMansionFromIndex,
    squareAreaOfMansionToIndex,

    formattedCompletionYMFrom,
    formattedCompletionYMTo,

    completionYMFromIndex,
    completionYMToIndex,

    formattedRoomsFrom,
    formattedRoomsTo,

    roomsFromIndex,
    roomsToIndex,

    resetButtonOn,
    okButtonOn,

    onEstatePriceChange,

    onTsuboAreaChange,
    onSquareAreaOfMansionChange,

    onCompletionYMIndexChange,

    onRoomsChange,

    onBldConditionSelectionsChange,

    onPetOkChange,
    onFlatChange,
    onForRentChange,
    onExceptForRentChange,
    onMyCompanyOnlyChange,
    onVenderOnlyChange,
    onStructureSummariesChange,
    onPurposeSummaryBsChange,

    onAlsoCreatedAtFromChange,
    onAlsoCreatedAtToChange,
    onAlsoPricedAtFromChange,
    onAlsoPricedAtToChange,

    onResetButtonClick,
    onOkButtonClick,
    onBackPaperClick
  } as const;
};

export type UseEstateFilterDialogHooksType = ReturnType<
  typeof useEstateFilterDialogHooks
>;
